/**
 * DXF `102` control-group nesting-depth tracking — single source of truth.
 *
 * An OBJECTS/ENTITIES record can embed `102` control blocks (`{ACAD_REACTORS`,
 * `{ACAD_XDICTIONARY`, `{BLKREFS`, …) that open with `102 "{NAME"` and close
 * with `102 "}"`. Handles (e.g. group 330 / 360) that appear INSIDE such a
 * block are cross-links, NOT the owner — so any parser that wants the
 * "depth-0" handle must track the open/close nesting while scanning groups.
 *
 * This was copy-pasted in `parseDynamicBlockObject` (sectionParsers) and twice
 * in `assocArrayParser` (decodeAssocArrayObject, decodeDependencyRef). The
 * logic is one CAD truth; keep it here so all call sites agree.
 */
import type { DxfGroup } from "./types";

/**
 * If `group` is a `102` control-group marker, return the new nesting depth
 * (incremented on `{…`, clamped-decremented on `}`); otherwise return
 * `undefined` so the caller can fall through to its own group handling.
 *
 * Callers use the `undefined` result as the "not a control marker, keep
 * processing" signal — mirroring the original inline `if (g.code === 102) { … ;
 * continue; }` shape exactly.
 */
export function advanceControlDepth(
  group: DxfGroup,
  depth: number,
): number | undefined {
  if (group.code !== 102) return undefined;
  const token = group.value.trim();
  if (token.startsWith("{")) return depth + 1;
  // A `102` close marker is exactly "}". Exact equality (not startsWith) so a
  // malformed `}foo` is left untouched — matches the original inline logic.
  // eslint-disable-next-line security/detect-possible-timing-attacks -- DXF token literal, not a secret comparison
  if (token === "}") return Math.max(0, depth - 1);
  return depth;
}
